import React, {useState, useRef, useEffect, useCallback} from 'react'; // Added useCallback
import ReactDOM from 'react-dom';
import {FaHeadphones, FaMusic, FaPause, FaPlay, FaCrown, FaShare, FaCheck, FaStepBackward, FaStepForward, FaVolumeUp, FaWhatsapp, FaInstagram, FaTwitter, FaFacebookF, FaLink, FaTimes, FaRobot} from 'react-icons/fa';
import API_CONFIG from '../config/api';
import api from '../services/api';
import { encodeId } from '../utils/idMasker';
import './TrackList.css';

// Share Modal Component using Portal
const ShareModal = ({ track, onClose, onCopyLink, onSocialShare, copiedId }) => {
    return ReactDOM.createPortal(
        <div className="share-modal-overlay" onClick={onClose}>
            <div className="share-modal" onClick={(e) => e.stopPropagation()}>
                <div className="share-modal-header">
                    <span>Share Track</span>
                    <button className="share-modal-close" onClick={onClose}>
                        <FaTimes />
                    </button>
                </div>
                <div className="share-modal-options">
                    <button className="share-option whatsapp" onClick={(e) => onSocialShare(track, 'whatsapp', e)}>
                        <FaWhatsapp /> WhatsApp
                    </button>
                    <button className="share-option instagram" onClick={(e) => onSocialShare(track, 'instagram', e)}>
                        <FaInstagram /> Instagram
                    </button>
                    <button className="share-option twitter" onClick={(e) => onSocialShare(track, 'twitter', e)}>
                        <FaTwitter /> Twitter / X
                    </button>
                    <button className="share-option facebook" onClick={(e) => onSocialShare(track, 'facebook', e)}>
                        <FaFacebookF /> Facebook
                    </button>
                    <button className="share-option copy-link" onClick={(e) => onCopyLink(track, e)}>
                        <FaLink /> {copiedId === track.id ? 'Copied!' : 'Copy Link'}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

const TrackList = ({tracks = [], onTrackPlay, onPaywallCheck, loading = false}) => {
    const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
    const [imageErrors, setImageErrors] = useState({});
    const [copiedId, setCopiedId] = useState(null);
    const [sharePopupId, setSharePopupId] = useState(null);
    const [currentTrack, setCurrentTrack] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const audioRef = useRef(null);
    const loadingTrackRef = useRef(null);

    // Format time in MM:SS
    const formatTime = (seconds) => {
        if (isNaN(seconds)) return '0:00';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Build share URL for a track
    const getShareUrl = (track) => {
        const pageSlug = track.page_slug || 'track';
        const trackId = track.track?.id || track.id;
        const encodedId = encodeId(trackId);
        return `${window.location.origin}/track/${pageSlug}/${encodedId}`;
    };

    // Toggle share popup
    const handleShareToggle = (track, e) => {
        e.stopPropagation();
        setSharePopupId(sharePopupId === track.id ? null : track.id);
    };

    // Copy link to clipboard
    const handleCopyLink = async (track, e) => {
        e.stopPropagation();
        const shareUrl = getShareUrl(track);
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopiedId(track.id);
            setTimeout(() => { setCopiedId(null); setSharePopupId(null); }, 1500);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    // Share to social platform
    const handleSocialShare = (track, platform, e) => {
        e.stopPropagation();
        const shareUrl = encodeURIComponent(getShareUrl(track));
        const title = encodeURIComponent(track.title || 'Check out this track');
        let url = '';
        switch (platform) {
            case 'whatsapp':
                url = `https://wa.me/?text=${title}%20${shareUrl}`;
                break;
            case 'twitter':
                url = `https://twitter.com/intent/tweet?text=${title}&url=${shareUrl}`;
                break;
            case 'facebook':
                url = `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`;
                break;
            case 'instagram':
                handleCopyLink(track, e);
                return;
            default:
                return;
        }
        window.open(url, '_blank', 'noopener,noreferrer,width=600,height=400');
        setSharePopupId(null);
    };

    // Close share popup when clicking outside
    useEffect(() => {
        const handleClickOutside = () => setSharePopupId(null);
        if (sharePopupId !== null) {
            document.addEventListener('click', handleClickOutside);
            return () => document.removeEventListener('click', handleClickOutside);
        }
    }, [sharePopupId]);

    // Handle track play/pause from card - Memoized with useCallback
    const handlePlayClick = useCallback(async (track) => {
        try {
            if (currentlyPlaying === track.id && isPlaying) {
                // Pause current track
                audioRef.current?.pause();
            } else if (currentlyPlaying === track.id && !isPlaying) {
                // Resume current track
                audioRef.current?.play();
            } else {
                // Paywall check before streaming a new paid track
                if (track.is_paid && onPaywallCheck) {
                    const hasAccess = await onPaywallCheck(track);
                    if (!hasAccess) return;
                }

                // Immediately stop ALL audio elements on page to prevent overlap
                document.querySelectorAll('audio').forEach(a => { a.pause(); a.currentTime = 0; });
                // Notify other TrackList instances to reset their UI state
                window.dispatchEvent(new CustomEvent('tracklist-play', { detail: { trackId: track.id } }));
                // Mark this track as the one loading (race condition guard)
                loadingTrackRef.current = track.id;
                setCurrentTrack(track);
                setCurrentlyPlaying(track.id);
                setIsPlaying(false);
                setCurrentTime(0);
                setDuration(0);
                // Call stream API to get the playable URL
                const postId = track.id;
                const tracksResponse = await api.get('/tracks/');
                const trackRecord = tracksResponse.data.tracks?.find(t => t.post_id === postId);
                if (!trackRecord) { console.error('No track found for post ID:', postId); return; }
                // Guard: abort if a newer track was clicked while we were loading
                if (loadingTrackRef.current !== track.id) return;
                const streamResponse = await api.get('/tracks/' + trackRecord.id + '/stream');
                const streamUrl = streamResponse.data.stream_url;
                if (!streamUrl) { console.error('No stream URL in response'); return; }
                // Guard again after second API call
                if (loadingTrackRef.current !== track.id) return;
                if (audioRef.current) {
                    audioRef.current.src = streamUrl;
                    audioRef.current.load();
                    try {
                        await audioRef.current.play();
                    } catch (error) {
                        console.error('Error playing audio:', error);
                    }
                }
            }
        } catch (error) {
            console.error('Error playing track:', error);
        }
    }, [currentlyPlaying, isPlaying, onPaywallCheck]);

    // Bottom player controls
    const handlePlayPause = () => {
        if (!audioRef.current) return;
        if (isPlaying) {
            audioRef.current.pause();
        } else {
            audioRef.current.play();
        }
    };

    // Memoize handleNext with handlePlayClick as dependency
    const handleNext = useCallback(() => {
        if (!currentTrack) return;
        const currentIndex = tracks.findIndex(t => t.id === currentTrack.id);
        if (currentIndex < tracks.length - 1) {
            handlePlayClick(tracks[currentIndex + 1]);
        }
    }, [currentTrack, tracks, handlePlayClick]); // Add handlePlayClick to dependencies

    // Memoize handlePrevious with handlePlayClick as dependency
    const handlePrevious = useCallback(() => {
        if (!currentTrack) return;
        const currentIndex = tracks.findIndex(t => t.id === currentTrack.id);
        if (currentIndex > 0) {
            handlePlayClick(tracks[currentIndex - 1]);
        }
    }, [currentTrack, tracks, handlePlayClick]); // Add handlePlayClick to dependencies

    const handleProgressClick = (e) => {
        if (!audioRef.current || !duration) return;
        const progressBar = e.currentTarget;
        const rect = progressBar.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const percentage = clickX / rect.width;
        audioRef.current.currentTime = percentage * duration;
    };

    const handleVolumeChange = (e) => {
        const newVolume = parseFloat(e.target.value);
        setVolume(newVolume);
        if (audioRef.current) {
            audioRef.current.volume = newVolume;
        }
    };

    // Audio event listeners
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
        const handleLoadedMetadata = () => setDuration(audio.duration);
        const handlePlay = () => setIsPlaying(true);
        const handlePause = () => setIsPlaying(false);
        const handleEnded = () => {
            setIsPlaying(false);
            handleNext(); // Now this is properly memoized and included in dependencies
        };

        audio.addEventListener('timeupdate', handleTimeUpdate);
        audio.addEventListener('loadedmetadata', handleLoadedMetadata);
        audio.addEventListener('play', handlePlay);
        audio.addEventListener('pause', handlePause);
        audio.addEventListener('ended', handleEnded);

        // Reset this instance when another TrackList starts playing
        const handleOtherPlay = () => {
            if (!audioRef.current || audioRef.current.paused) {
                setCurrentlyPlaying(null);
                setIsPlaying(false);
                setCurrentTrack(null);
            }
        };
        window.addEventListener('tracklist-play', handleOtherPlay);

        return () => {
            audio.removeEventListener('timeupdate', handleTimeUpdate);
            audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
            audio.removeEventListener('play', handlePlay);
            audio.removeEventListener('pause', handlePause);
            audio.removeEventListener('ended', handleEnded);
            window.removeEventListener('tracklist-play', handleOtherPlay);
        };
    }, [handleNext]); // Add handleNext to the dependency array

    // Handle image load errors
    const handleImageError = (trackId) => {
        setImageErrors(prev => ({...prev, [trackId]: true}));
    };

    // Construct full image URL from relative path
    const getImageUrl = (coverImageUrl) => {
        if (!coverImageUrl) return null;
        // If it's already a full URL, return as is
        if (coverImageUrl.startsWith('http://') || coverImageUrl.startsWith('https://')) {
            return coverImageUrl;
        }
        // Otherwise, prepend the base URL
        return `${API_CONFIG.BASE_URL}${coverImageUrl}`;
    };

    if (loading) {
        return (<div className="track-list-container">
                <div className="track-list-loading">
                    <div className="loading-spinner"></div>
                    <p>Loading tracks...</p>
                </div>
            </div>);
    }

    return (<div className="track-list-container">
            {/* Tracks Grid/List */}
            {tracks.length > 0 ? (<div className="track-grid">
                    {tracks.map((track) => (<div 
                            key={track.id} 
                            className="track-item"
                            data-premium={track.is_paid ? "true" : "false"}
                            data-required-credits={track.is_paid ? (track.price || 1) : 0}
                        >
                            {/* Track Cover Image */}
                            <div className="track-cover">
                                {!imageErrors[track.id] && track.cover_image_url ? (<img
                                        src={getImageUrl(track.cover_image_url)}
                                        alt={`${track.title} cover`}
                                        onError={() => handleImageError(track.id)}
                                    />) : (<div className="track-cover-placeholder">
                                        <FaMusic/>
                                    </div>)}

                                {/* Play Button Overlay */}
                                <button
                                    className={`play-button ${currentlyPlaying === track.id && isPlaying ? 'playing' : ''}`}
                                    onClick={() => handlePlayClick(track)}
                                    aria-label={currentlyPlaying === track.id && isPlaying ? 'Pause track' : 'Play track'}
                                >
                                    {currentlyPlaying === track.id && isPlaying ? <FaPause/> : <FaPlay/>}
                                </button>
                            </div>

                            {/* Track Info */}
                            <div className="track-info">
                                {/* Paid/Free Badge */}
                                {track.is_paid && (
                                    <span className="premium-badge">
                                        <FaCrown className="badge-icon" />
                                        Premium
                                    </span>
                                )}

                                <h3 className="track-title">{track.title || 'Untitled'}</h3>
                                <p className="track-description">
                                    {track.description }
                                </p>

                                {/* Track Meta */}
                                <div className="track-meta">
                                    {/* Duration */}
                                    {(track.track?.duration_formatted || track.duration_formatted) && (
                                        <span className="track-duration">
                                            {track.track?.duration_formatted || track.duration_formatted}
                                        </span>
                                    )}

                                    {/* Genre */}
                                    {(track.track?.genre || track.genre) && (
                                        <span className="track-genre">
                                            {track.track?.genre || track.genre}
                                        </span>
                                    )}
                                </div>

                                {/* Track Stats */}
                                <div className="track-stats">
                                    {(() => {
                                        const raw = track.track?.ai_score ?? track.ai_score;
                                        if (raw === undefined || raw === null) return null;
                                        const n = Number(raw);
                                        if (!Number.isFinite(n) || n < 0) return null;
                                        return (
                                            <span className="stat-item track-ai-meter" title={`AI Score: ${n}/5`}>
                                                <FaRobot className="stat-icon" aria-hidden />
                                                {n}/5
                                            </span>
                                        );
                                    })()}
                                    {/* Play Count */}
                                    <span className="stat-item">
                                        <FaHeadphones className="stat-icon"/>
                                        {(track.track?.play_count ?? track.play_count) || 0} Listened
                                    </span>
                                    {/* Share Button */}
                                    <button
                                        className={`share-btn ${copiedId === track.id ? 'copied' : ''}`}
                                        onClick={(e) => handleShareToggle(track, e)}
                                        title="Share this track"
                                    >
                                        {copiedId === track.id ? <FaCheck /> : <FaShare />}
                                        {copiedId === track.id ? 'Copied!' : 'Share'}
                                    </button>
                                    {sharePopupId === track.id && (
                                        <ShareModal
                                            track={track}
                                            onClose={() => setSharePopupId(null)}
                                            onCopyLink={handleCopyLink}
                                            onSocialShare={handleSocialShare}
                                            copiedId={copiedId}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>))}
                </div>) : (/* Empty State */
                <div className="track-list-empty">
                    <FaMusic className="empty-icon"/>
                    <h3>No Tracks Found</h3>
                </div>)}

            {/* Bottom Sticky Player */}
            {currentTrack && (
                <div className="bottom-player">
                    <div className="bottom-player-content">
                        {/* Track Info */}
                        <div className="player-track-info">
                            <div className="player-thumbnail">
                                {currentTrack.cover_image_url ? (
                                    <img src={getImageUrl(currentTrack.cover_image_url)} alt={currentTrack.title} />
                                ) : (
                                    <FaMusic />
                                )}
                            </div>
                            <div className="player-text">
                                <div className="player-title">{currentTrack.title || 'Untitled'}</div>
                                <div className="player-description">
                                    {currentTrack.description }
                                </div>
                            </div>
                        </div>

                        {/* Playback Controls */}
                        <div className="player-controls">
                            <button className="control-btn" onClick={handlePrevious}>
                                <FaStepBackward />
                            </button>
                            <button className="control-btn play-pause-btn" onClick={handlePlayPause}>
                                {isPlaying ? <FaPause /> : <FaPlay />}
                            </button>
                            <button className="control-btn" onClick={handleNext}>
                                <FaStepForward />
                            </button>
                        </div>

                        {/* Progress Bar and Volume Control */}
                        <div className="player-progress-volume-container">
                            <div className="player-progress">
                                <span className="player-time">{formatTime(currentTime)}</span>
                                <div className="player-progress-bar" onClick={handleProgressClick}>
                                    <div 
                                        className="player-progress-fill"
                                        style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
                                    >
                                        <div className="player-progress-thumb"></div>
                                    </div>
                                </div>
                                <span className="player-time">{formatTime(duration)}</span>
                            </div>

                            {/* Volume Control */}
                            <div className="player-volume">
                                <FaVolumeUp />
                                <input 
                                    type="range" 
                                    min="0" 
                                    max="1" 
                                    step="0.01" 
                                    value={volume}
                                    onChange={handleVolumeChange}
                                    className="volume-slider"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Hidden Audio Element */}
            <audio ref={audioRef} preload="metadata" />
        </div>);
};

export default TrackList;